<script setup lang="ts">
import { computed } from 'vue'
import {
  KeyIcon,
  HomeIcon,
  DocumentTextIcon,
  Squares2X2Icon,
  Cog6ToothIcon,
  QueueListIcon,
  PencilSquareIcon
} from '@heroicons/vue/24/outline'
import { useHelpStore } from '@/stores/useHelpStore'
import type { HelpPage } from '@/stores/useHelpStore'
import type { Component } from 'vue'

const helpStore = useHelpStore()

const iconMap: Record<string, Component> = {
  'key': KeyIcon,
  'home': HomeIcon,
  'document-text': DocumentTextIcon,
  'squares-2x2': Squares2X2Icon,
  'cog-6-tooth': Cog6ToothIcon,
  'queue-list': QueueListIcon,
  'pencil-square': PencilSquareIcon
}

function getIcon(iconName: string): Component {
  return iconMap[iconName] || DocumentTextIcon
}

function isActive(page: HelpPage): boolean {
  return page.route === helpStore.currentRouteName
}

function isAdminPage(page: HelpPage): boolean {
  return page.route.startsWith('admin')
}

const standardPages = computed(() =>
  helpStore.sortedPages.filter(p => !isAdminPage(p))
)

const adminPages = computed(() =>
  helpStore.sortedPages.filter(p => isAdminPage(p))
)

function handleNavigate(routeName: string) {
  helpStore.navigateTo(routeName)
}
</script>

<template>
  <nav class="help-nav">
    <div class="nav-header">
      <h3 class="nav-title">Sommaire</h3>
    </div>

    <div class="nav-list">
      <!-- Standard pages -->
      <button
        v-for="page in standardPages"
        :key="page.route"
        type="button"
        class="nav-item"
        :class="{ 'nav-item--active': isActive(page) }"
        @click="handleNavigate(page.route)"
      >
        <component :is="getIcon(page.icon)" class="nav-icon" />
        <span class="nav-label">{{ page.title }}</span>
      </button>

      <!-- Separator -->
      <div v-if="adminPages.length > 0" class="nav-separator">
        <span class="separator-label">Administration</span>
      </div>

      <!-- Admin pages -->
      <button
        v-for="page in adminPages"
        :key="page.route"
        type="button"
        class="nav-item"
        :class="{ 'nav-item--active': isActive(page) }"
        @click="handleNavigate(page.route)"
      >
        <component :is="getIcon(page.icon)" class="nav-icon" />
        <span class="nav-label">{{ page.title }}</span>
      </button>
    </div>
  </nav>
</template>

<style scoped>
.help-nav {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.nav-header {
  padding: 16px 16px 12px;
  border-bottom: 1px solid #e9ecef;
}

.nav-title {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
}

.nav-list {
  padding: 8px;
  flex: 1;
  overflow-y: auto;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 48px;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #4b5563;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  font-size: 0.9375rem;
  border-left: 2px solid transparent;
}

.nav-item:hover {
  background: #f3f4f6;
  color: #212529;
}

.nav-item--active {
  background: rgba(111, 59, 148, 0.1);
  color: #6F3B94;
  font-weight: 500;
  border-left-color: #6F3B94;
}

.nav-item:focus-visible {
  outline: 2px solid #0066CC;
  outline-offset: -2px;
}

.nav-icon {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
}

.nav-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-separator {
  padding: 12px 12px 4px;
  margin-top: 4px;
}

.separator-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
</style>
